import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { config } from '../config';
import { withImpersonationHeader } from '../utils/impersonation';

interface SessionItem {
  id: string;
  session_id: string;  // Frontend uses session_id
  context_id?: string;  // Backend API field (for compatibility)
  display_name: string;
  description?: string;
  last_url?: string;
  created_at: string;
  platform_id?: string | null;
  platform_name?: string | null;
}

interface AccountItem {
  id: string;
  platform_id: string;
  platform_name?: string;
  browserbase_context_id?: string;
  context_id?: string;
  context_display_name?: string;
  username?: string;
  is_active?: boolean;
  created_at?: string;
}

interface PlatformItem {
  id: string;
  platform_name: string;
  login_script_s3_key?: string;
  login_url?: string;
  created_at?: string;
}

const LoginAutomation: React.FC = () => {
  const { session } = useAuth();
  const [sessions, setSessions] = useState<SessionItem[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string>('');
  const [hyperSelectedSessionId, setHyperSelectedSessionId] = useState<string>('');
  const [hyperSessionId, setHyperSessionId] = useState<string | null>(null);
  const [hyperSessionContextId, setHyperSessionContextId] = useState<string | null>(null);
  const [hyperDebugUrl, setHyperDebugUrl] = useState<string | null>(null);
  const [hyperLoading, setHyperLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [working, setWorking] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [debugUrl, setDebugUrl] = useState<string | null>(null);
  const [finishing, setFinishing] = useState(false);
  const [closingSession, setClosingSession] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // Auto-hide blue notice after 5 seconds
  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(null), 5000);
    return () => clearTimeout(timer);
  }, [notice]);
  const [recordingStarted, setRecordingStarted] = useState(false);
  const [startingRecorder, setStartingRecorder] = useState(false);
  const [accounts, setAccounts] = useState<AccountItem[]>([]);
  const [platforms, setPlatforms] = useState<PlatformItem[]>([]);
  const [activeTab, setActiveTab] = useState<'create' | 'test' | 'hyperbrowser' | 'accounts' | 'platforms'>('create');
  const [platformName, setPlatformName] = useState('');
  const [platformUrl, setPlatformUrl] = useState('');
  const [platformAction, setPlatformAction] = useState<'create'|'bind'>('create');
  const [selectedPlatformId, setSelectedPlatformId] = useState<string>('');
  const [testPlatformId, setTestPlatformId] = useState<string>('');
  const [testAccountId, setTestAccountId] = useState<string>('');
  const [testSessionId, setTestSessionId] = useState<string | null>(null);
  const [testLiveViewUrl, setTestLiveViewUrl] = useState<string | null>(null);
  const [testLoading, setTestLoading] = useState(false);
  const [testStatus, setTestStatus] = useState<'idle' | 'launching' | 'running' | 'success' | 'error'>('idle');
  const [testResult, setTestResult] = useState<any | null>(null);
  const [testMode, setTestMode] = useState<'account' | 'session'>('account');
  const [testSessionBrowserbaseId, setTestSessionBrowserbaseId] = useState<string>('');
  const [manualUsername, setManualUsername] = useState('');
  const [manualPassword, setManualPassword] = useState('');
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [accountModalMode, setAccountModalMode] = useState<'create' | 'edit'>('create');
  const [newAccountSessionId, setNewAccountSessionId] = useState('');
  const [newAccountDisplayName, setNewAccountDisplayName] = useState('');
  const [newAccountPlatformId, setNewAccountPlatformId] = useState('');
  const [newAccountUsername, setNewAccountUsername] = useState('');
  const [newAccountPassword, setNewAccountPassword] = useState('');
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [accountModalError, setAccountModalError] = useState<string | null>(null);
  const [showPlatformModal, setShowPlatformModal] = useState(false);
  const [platformModalMode, setPlatformModalMode] = useState<'create' | 'edit'>('create');
  const [platformModalId, setPlatformModalId] = useState<string | null>(null);
  const [platformModalName, setPlatformModalName] = useState('');
  const [platformModalUrl, setPlatformModalUrl] = useState('');
  const [platformModalKey, setPlatformModalKey] = useState('');
  const [savingPlatform, setSavingPlatform] = useState(false);
  const [platformModalError, setPlatformModalError] = useState<string | null>(null);

  const [showHyperSaveDialog, setShowHyperSaveDialog] = useState(false);
  const [hyperContextName, setHyperContextName] = useState('');
  const [hyperContextSaving, setHyperContextSaving] = useState(false);
  const [hyperContextCanceling, setHyperContextCanceling] = useState(false);
  const [hyperContextToSave, setHyperContextToSave] = useState<{ sessionId: string; contextId: string; currentUrl?: string } | null>(null);

  const getAuthHeaders = (): HeadersInit => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }
    return withImpersonationHeader(headers);
  };

  const testPlatform = platforms.find(p => p.id === testPlatformId);
  const testAccount = accounts.find(acc => acc.id === testAccountId);
  const selectedTestSession = sessions.find(sess => sess.session_id === testSessionBrowserbaseId);
  const canRunTest = testMode === 'account'
    ? Boolean(testPlatform && testAccount && testAccountId && !testLoading)
    : Boolean(testPlatform && testSessionBrowserbaseId && manualUsername.trim() && manualPassword && !testLoading);
  const selectedSession = sessions.find(sess => sess.session_id === selectedSessionId);

  const loadSessions = async () => {
    if (!session?.access_token) return;
    setLoading(true);
    try {
      const res = await fetch(config.getApiUrl('/browser/contexts'), {
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      // Map backend context_id to frontend session_id
      const items: SessionItem[] = (data.contexts || []).map((item: any) => ({
        ...item,
        session_id: item.context_id,  // Map context_id to session_id for frontend use
      }));
      setSessions(items);
      if (items.length && !selectedSessionId) setSelectedSessionId(items[0].session_id);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (session?.access_token) loadSessions();
  }, [session?.access_token]);

  const loadAccounts = async () => {
    if (!session?.access_token) return;
    try {
      const res = await fetch(config.getApiUrl('/browser/accounts'), { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      const items: AccountItem[] = data.accounts || [];
      setAccounts(items);
      if (items.length && !testAccountId) setTestAccountId(items[0].id);
    } catch (e) {
      // best-effort; don't block page
    }
  };

  useEffect(() => {
    if (session?.access_token) loadAccounts();
  }, [session?.access_token]);

  useEffect(() => {
    if (!testPlatformId) return;
    const matching = accounts.find(acc => acc.platform_id === testPlatformId);
    if (matching) {
      if (testAccountId && accounts.some(acc => acc.id === testAccountId && acc.platform_id === testPlatformId)) {
        return;
      }
      setTestAccountId(matching.id);
    } else {
      setTestAccountId('');
    }
  }, [testPlatformId, accounts, testAccountId]);

  useEffect(() => {
    if (testMode === 'account') {
      setTestSessionBrowserbaseId('');
      setManualUsername('');
      setManualPassword('');
    }
  }, [testMode]);

  const loadPlatforms = async () => {
    if (!session?.access_token) return;
    try {
      const res = await fetch(config.getApiUrl('/browser/platforms'), { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setPlatforms(data.platforms || []);
    } catch (e) {
      // ignore silently
    }
  };

  useEffect(() => {
    if (session?.access_token) loadPlatforms();
  }, [session?.access_token]);

  const hyperSelectedSession = sessions.find(sess => sess.session_id === hyperSelectedSessionId);

  const saveHyperContext = async () => {
    if (!hyperContextToSave || !hyperContextName.trim() || !session?.access_token || hyperContextSaving) return;

    setHyperContextSaving(true);
    
    try {
      const response = await fetch(config.getApiUrl('/browser/contexts'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          context_id: hyperContextToSave.contextId,
          display_name: hyperContextName.trim(),
          description: `Saved from browser session ${hyperContextToSave.sessionId.slice(0, 8)}`,
          last_url: hyperContextToSave.currentUrl
        })
      });

      if (response.ok) {
        const data = await response.json();
        setNotice(`Session saved as: ${hyperContextName.trim()}`);
        await loadSessions();
        
        // Now close the browser session after saving
        const sessionToClose = hyperContextToSave.sessionId;
        setShowHyperSaveDialog(false);
        setHyperContextName('');
        setHyperContextToSave(null);
        
        // Actually close the browser session
        if (sessionToClose) {
          await fetch(config.getApiUrl(`/browser/session/${sessionToClose}`), {
            method: 'DELETE',
            headers: getAuthHeaders(),
            credentials: 'include'
          }).catch(() => {});
        }
        
        // Clean up browser session state
        setHyperSessionId(null);
        setHyperSessionContextId(null);
        setHyperDebugUrl(null);
      } else {
        const errorData = await response.json();
        setError(`Failed to save session: ${errorData.detail}`);
      }
    } catch (error) {
      console.error('Error saving session:', error);
      setError(`Error saving session: ${error}`);
    } finally {
      setHyperContextSaving(false);
    }
  };

  const cancelHyperContextSaving = async () => {
    setHyperContextCanceling(true);
    
    try {
      // Close the browser session without saving
      const sessionToClose = hyperContextToSave?.sessionId;
      setShowHyperSaveDialog(false);
      setHyperContextName('');
      setHyperContextToSave(null);
      
      // Actually close the browser session now
      if (sessionToClose) {
        await fetch(config.getApiUrl(`/browser/session/${sessionToClose}`), {
          method: 'DELETE',
          headers: getAuthHeaders(),
          credentials: 'include'
        }).catch(() => {});
        setNotice('Browser session closed without saving.');
      }
      
      // Clean up browser session state
      setHyperSessionId(null);
      setHyperSessionContextId(null);
      setHyperDebugUrl(null);
    } catch (error) {
      console.error('Error during session cancellation:', error);
    } finally {
      setHyperContextCanceling(false);
    }
  };

  const launchHyperbrowserSession = async () => {
    if (!session?.access_token) return;

    setHyperLoading(true);
    setNotice(null);
    setError(null);

    try {
      if (hyperSessionId) {
        await stopHyperbrowserSession(true);
      }

      const convId = `login_hyper_${Date.now()}`;
      const payload: Record<string, unknown> = {
        conversation_id: convId,
      };

      // If a session is selected, use it; otherwise create new browser session
      if (hyperSelectedSessionId) {
        payload.context_id = hyperSelectedSessionId;
      }

      const res = await fetch(config.getApiUrl('/browser/session'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();

      if (!data.session_id || !data.debug_url) {
        throw new Error('Session response missing session or live view URL');
      }

      setHyperSessionId(data.session_id);
      setHyperSessionContextId(data.context_id || hyperSelectedSessionId || null);
      setHyperDebugUrl(data.debug_url);
      
      if (hyperSelectedSessionId) {
        setNotice(`Browser session launched for session "${hyperSelectedSession?.display_name || hyperSelectedSessionId}".`);
      } else {
        setNotice('New browser session launched.');
      }
    } catch (e: any) {
      setHyperSessionId(null);
      setHyperSessionContextId(null);
      setHyperDebugUrl(null);
      setError(e?.message || 'Failed to launch browser session');
    } finally {
      setHyperLoading(false);
    }
  };

  const stopHyperbrowserSession = async (silent = false) => {
    if (!hyperSessionId) return;

    // If this was a brand new browser session (no saved session selected), mimic main page:
    // call terminate endpoint to fetch context_id/current_url, then prompt to save.
    if (!silent && !hyperSelectedSessionId) {
      setHyperLoading(true);
      try {
        const res = await fetch(config.getApiUrl(`/browser/session/${hyperSessionId}/terminate`), {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include'
        });
        if (res.ok) {
          const data = await res.json();
          const fetchedContextId = data?.context_id || hyperSessionContextId;
          const currentUrl = data?.current_url || undefined;
          if (fetchedContextId) {
            setHyperSessionContextId(fetchedContextId);
            setHyperContextToSave({
              sessionId: hyperSessionId,
              contextId: fetchedContextId,
              currentUrl
            });
            setHyperContextName('');
            setShowHyperSaveDialog(true);
            setHyperLoading(false);
            return; // Wait for user to Save or Skip before fully closing
          }
        }
      } catch (err) {
        // Best effort; fall through to hard close
        console.error('Terminate (keep-alive) failed:', err);
      } finally {
        // If we didn't early-return due to dialog, continue to close below
      }
    }

    setHyperLoading(true);
    try {
      await fetch(config.getApiUrl(`/browser/session/${hyperSessionId}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      }).catch(() => {});
      if (!silent) {
        setNotice('Browser session closed.');
      }
    } catch (e) {
      if (!silent) {
        setError('Failed to stop browser session');
      }
    } finally {
      setHyperSessionId(null);
      setHyperSessionContextId(null);
      setHyperDebugUrl(null);
      setHyperLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (hyperSessionId) {
        stopHyperbrowserSession(true).catch(() => {});
      }
    };
  }, [hyperSessionId]);

  useEffect(() => {
    if (activeTab !== 'hyperbrowser' && hyperSessionId) {
      stopHyperbrowserSession(true).catch(() => {});
    }
  }, [activeTab, hyperSessionId]);

  const openAccountModal = (contextId?: string, account?: AccountItem) => {
    const initialPlatformId = account?.platform_id || selectedPlatformId || platforms[0]?.id || '';
    setAccountModalMode(account ? 'edit' : 'create');

    if (account) {
      const existingSessionId = account.context_id || contextId || account.browserbase_context_id || '';
      const matchedSession = sessions.find(sess => (sess.context_id || sess.session_id) === existingSessionId);
      setNewAccountSessionId(existingSessionId);
      setNewAccountDisplayName(matchedSession?.display_name || matchedSession?.platform_name || '');
    } else {
      setNewAccountSessionId('');
      const selectedPlatform = platforms.find(p => p.id === initialPlatformId);
      setNewAccountDisplayName(selectedPlatform ? `${selectedPlatform.platform_name} Login Session` : '');
    }

    setNewAccountPlatformId(initialPlatformId);
    setNewAccountUsername(account?.username || '');
    setNewAccountPassword('');
    setAccountModalError(null);
    setShowAccountModal(true);
  };

  const closeAccountModal = () => {
    setShowAccountModal(false);
    setAccountModalError(null);
    setAccountModalMode('create');
    setNewAccountSessionId('');
    setNewAccountDisplayName('');
    setNewAccountPlatformId('');
    setNewAccountUsername('');
    setNewAccountPassword('');
  };

  const handleAccountModalSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (creatingAccount) return;

    if (!newAccountPlatformId || !newAccountUsername.trim() || !newAccountPassword) {
      setAccountModalError('Platform, username, and password are required.');
      return;
    }

    if (accountModalMode === 'edit' && !newAccountSessionId) {
      setAccountModalError('Select a session to update.');
      return;
    }

    if (accountModalMode === 'edit' && !newAccountDisplayName.trim()) {
      setAccountModalError('Session name is required.');
      return;
    }

    setCreatingAccount(true);
    setAccountModalError(null);
    setError(null);
    let generatedSessionId = newAccountSessionId;
    let createdSessionId: string | null = null;

    try {
      const trimmedUsername = newAccountUsername.trim();
      const selectedPlatform = platforms.find(p => p.id === newAccountPlatformId);

      if (accountModalMode === 'create') {
        const defaultSessionName = selectedPlatform ? `${selectedPlatform.platform_name} Login Session` : 'New Login Session';
        const sessionDisplayName = newAccountDisplayName.trim() || defaultSessionName;

        const contextCreateRes = await fetch(config.getApiUrl('/browser/get-live-view-url'), {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            display_name: sessionDisplayName,
            description: `Session created for account ${trimmedUsername}`,
            platform_id: newAccountPlatformId || undefined,
          }),
        });

        if (!contextCreateRes.ok) {
          const errorText = await contextCreateRes.text().catch(() => '');
          throw new Error(errorText || 'Failed to create a new session for this account.');
        }

        const contextCreateData = await contextCreateRes.json().catch(() => null);
        generatedSessionId = contextCreateData?.context_id || contextCreateData?.context?.context_id || '';
        createdSessionId = contextCreateData?.session_id || null;

        if (!generatedSessionId) {
          throw new Error('Session creation succeeded but no context_id was returned.');
        }

        setNewAccountSessionId(generatedSessionId);
      } else {
        const trimmedDisplayName = newAccountDisplayName.trim();
        const matchedSession = sessions.find(sess => (sess.context_id || sess.session_id) === generatedSessionId);
        const existingDisplayName = (matchedSession?.display_name || '').trim();

        if (trimmedDisplayName && trimmedDisplayName !== existingDisplayName) {
          const updatePayload: Record<string, unknown> = {
            context_id: generatedSessionId,
            display_name: trimmedDisplayName,
          };
          if (matchedSession?.description) {
            updatePayload.description = matchedSession.description;
          }

          const updateRes = await fetch(config.getApiUrl('/browser/contexts'), {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(updatePayload)
          });
          if (!updateRes.ok) {
            const errorText = await updateRes.text().catch(() => '');
            throw new Error(errorText || 'Failed to update session name.');
          }
        }
      }

      if (!generatedSessionId) {
        throw new Error('Session identifier missing. Please create the account again.');
      }

      const res = await fetch(config.getApiUrl(`/browser/contexts/${generatedSessionId}/ensure-account`), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          username: trimmedUsername,
          password: newAccountPassword,
          platform_id: newAccountPlatformId
        })
      });
      if (!res.ok) throw new Error(await res.text());
      await res.json().catch(() => null);

      await loadAccounts();
      await loadSessions();
      if (accountModalMode === 'edit') {
        setNotice('Account and session updated.');
      } else {
        setNotice(`Account created with new session ${generatedSessionId}.`);
      }
      closeAccountModal();
    } catch (e: any) {
      setAccountModalError(e?.message || 'Failed to save account.');
    } finally {
      if (createdSessionId) {
        fetch(config.getApiUrl(`/browser/session/${createdSessionId}`), {
          method: 'DELETE',
          headers: getAuthHeaders(),
          credentials: 'include'
        }).catch(() => {});
      }
      setCreatingAccount(false);
    }
  };

  const handleDeleteAccount = async (accountId: string) => {
    if (!accountId) return;
    const confirmDelete = window.confirm('Delete this account? This cannot be undone.');
    if (!confirmDelete) return;

    try {
      const res = await fetch(config.getApiUrl(`/browser/accounts/${accountId}`), {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error(await res.text());
      await res.json().catch(() => null);
      setNotice('Account deleted.');
      setError(null);
      await loadAccounts();
      await loadSessions();
    } catch (e: any) {
      setError(e?.message || 'Failed to delete account');
    }
  };

  const openPlatformModal = (platform?: PlatformItem) => {
    setPlatformModalMode(platform ? 'edit' : 'create');
    setSavingPlatform(false);
    setPlatformModalId(platform?.id ?? null);
    setPlatformModalName(platform?.platform_name || '');
    setPlatformModalUrl(platform?.login_url || '');
    setPlatformModalKey(platform?.login_script_s3_key || '');
    setPlatformModalError(null);
    setShowPlatformModal(true);
  };

  const closePlatformModal = () => {
    setShowPlatformModal(false);
    setPlatformModalMode('create');
    setSavingPlatform(false);
    setPlatformModalId(null);
    setPlatformModalName('');
    setPlatformModalUrl('');
    setPlatformModalKey('');
    setPlatformModalError(null);
  };

  const handlePlatformModalSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (savingPlatform) return;

    if (!platformModalName.trim()) {
      setPlatformModalError('Platform name is required.');
      return;
    }

    setSavingPlatform(true);
    setPlatformModalError(null);

    try {
      const payload: Record<string, unknown> = {
        platform_name: platformModalName.trim(),
      };
      const trimmedUrl = platformModalUrl.trim();
      const trimmedKey = platformModalKey.trim();
      if (platformModalMode === 'edit') {
        payload.login_url = trimmedUrl;
        payload.login_script_s3_key = trimmedKey;
      } else {
        if (trimmedUrl) payload.login_url = trimmedUrl;
        if (trimmedKey) payload.login_script_s3_key = trimmedKey;
      }

      const endpoint = platformModalMode === 'edit' && platformModalId
        ? config.getApiUrl(`/browser/platforms/${platformModalId}`)
        : config.getApiUrl('/browser/platforms/manage');
      const method = platformModalMode === 'edit' && platformModalId ? 'PATCH' : 'POST';

      const res = await fetch(endpoint, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error(await res.text());
      await res.json().catch(() => null);

      await loadPlatforms();
      setNotice(platformModalMode === 'edit' ? 'Platform updated.' : 'Platform created.');
      setError(null);
      closePlatformModal();
    } catch (e: any) {
      setPlatformModalError(e?.message || 'Failed to save platform.');
    } finally {
      setSavingPlatform(false);
    }
  };

  const handleDeletePlatform = async (platformId: string) => {
    if (!platformId) return;
    const confirmDelete = window.confirm('Delete this platform? This cannot be undone.');
    if (!confirmDelete) return;

    try {
      const res = await fetch(config.getApiUrl(`/browser/platforms/${platformId}`), {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error(await res.text());
      await res.json().catch(() => null);
      setNotice('Platform deleted.');
      setError(null);
      await loadPlatforms();
    } catch (e: any) {
      setError(e?.message || 'Failed to delete platform');
    }
  };

  useEffect(() => {
    return () => {
      if (testSessionId) {
        const headers = getAuthHeaders();
        fetch(config.getApiUrl(`/browser/session/${testSessionId}`), {
          method: 'DELETE',
          headers,
          credentials: 'include'
        }).catch(() => {});
      }
    };
  }, [testSessionId]);

  const launchBrowserSession = async (): Promise<string | null> => {
    setWorking(true);
    setNotice(null);
    setError(null);
    try {
      // Create a new live view session using the selected session profile
      const convId = `login_automation_${Date.now()}`;
      const payload: Record<string, unknown> = {
        conversation_id: convId,
        toSave: Boolean(selectedSessionId)
      };
      if (selectedSessionId) {
        payload.context_id = selectedSessionId;
      }
      const res = await fetch(config.getApiUrl('/browser/session'), {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error(await res.text());
      const sess = await res.json();
      setSessionId(sess.session_id);
      setDebugUrl(sess.debug_url);
      setRecordingStarted(false);

      // If a Homepage URL is provided, navigate immediately
      const trimmedHomeUrl = (platformUrl || '').trim();
      if (trimmedHomeUrl) {
        try {
          await fetch(config.getApiUrl(`/browser/session/${sess.session_id}/navigate`), {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({ start_url: trimmedHomeUrl })
          });
        } catch (navErr) {
          console.warn('Failed to navigate to homepage URL after launch:', navErr);
        }
      }

      setNotice('Browser launched. Click Start Recording when ready.');
      return sess.session_id as string;
    } catch (e: any) {
      setError(e?.message || 'Failed to create login automation session');
      // Cleanup partial session if created
      if (sessionId) {
        try { await fetch(config.getApiUrl(`/browser/session/${sessionId}`), { method: 'DELETE', headers: getAuthHeaders(), credentials: 'include' }); } catch {}
      }
      setSessionId(null);
      setDebugUrl(null);
      setRecordingStarted(false);
      return null;
    } finally {
      setWorking(false);
    }
    return null;
  };

  const startRecorder = async (targetSessionId?: string) => {
    const sid = targetSessionId || sessionId;
    if (!sid || recordingStarted) return;
    setStartingRecorder(true);
    setError(null);
    setNotice(null);
    try {
      const res = await fetch(config.getApiUrl(`/browser/session/${sid}/login-recorder/start`), {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (!res.ok) throw new Error(await res.text());
      setRecordingStarted(true);
      setNotice('Recorder started. Complete your login in the embedded browser.');
    } catch (e: any) {
      setError(e?.message || 'Failed to start recorder');
    } finally {
      setStartingRecorder(false);
    }
  };

  const finishRecording = async () => {
    if (!sessionId) return;
    setFinishing(true);
    setError(null);
    try {
      // Stop recorder; retrieve S3 key
      const stopRes = await fetch(config.getApiUrl(`/browser/session/${sessionId}/login-recorder/stop`), {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (!stopRes.ok) throw new Error(await stopRes.text());
      const stopData = await stopRes.json();
      const s3Key = stopData.s3_key;
      if (!s3Key) throw new Error('No S3 key returned for saved login automation');
      // Bind to selected platform choice
      const effectiveUrl = platformUrl || (sessions.find(c => c.context_id === selectedSessionId)?.last_url || undefined);
      if (platformAction === 'create') {
        if (!platformName.trim()) throw new Error('Platform name is required');
        const res = await fetch(config.getApiUrl('/browser/platforms'), {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            context_id: selectedSessionId,
            platform_name: platformName.trim(),
            login_script_s3_key: s3Key,
            platform_url: effectiveUrl
          })
        });
        if (!res.ok) throw new Error(await res.text());
        setNotice('Platform object created and login script bound.');
      } else {
        if (!selectedPlatformId) throw new Error('Select a platform to bind');
        const res = await fetch(config.getApiUrl(`/browser/platforms/${selectedPlatformId}/login-script`), {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            login_script_s3_key: s3Key,
            platform_url: effectiveUrl,
            context_id: selectedSessionId
          })
        });
        if (!res.ok) throw new Error(await res.text());
        setNotice('Login script bound to existing platform.');
      }

      // Give the live view a brief moment before tearing down the session so the final page can render
      await new Promise(resolve => setTimeout(resolve, 3000));

      // Close the live session
      try {
        await fetch(config.getApiUrl(`/browser/session/${sessionId}`), {
          method: 'DELETE',
          headers: getAuthHeaders(),
          credentials: 'include'
        });
      } catch {}
      // Reset linking fields
      setRecordingStarted(false);
      setPlatformName('');
      setSelectedPlatformId('');
      setPlatformAction('create');
      setPlatformUrl('');
      setSessionId(null);
      setDebugUrl(null);
      loadPlatforms();
      loadAccounts();
      loadSessions();
    } catch (e: any) {
      setError(e?.message || 'Failed to finish and bind login automation');
    } finally {
      setFinishing(false);
    }
  };

  const closeRecordingSession = async (customNotice?: string) => {
    if (!sessionId) return;
    setClosingSession(true);
    setError(null);
    try {
      const res = await fetch(config.getApiUrl(`/browser/session/${sessionId}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (!res.ok) throw new Error(await res.text());
      setNotice(customNotice || 'Closed live view session.');
      setSessionId(null);
      setDebugUrl(null);
      setRecordingStarted(false);
      await loadSessions();
    } catch (e: any) {
      setError(e?.message || 'Failed to close live view session');
    } finally {
      setClosingSession(false);
    }
  };

  const cancelRecording = async () => {
    if (!sessionId || closingSession || finishing) return;
    await closeRecordingSession('Recording cancelled.');
  };

  const handlePrimaryAction = async () => {
    if (recordingStarted) {
      await finishRecording();
      return;
    }

    if (working || startingRecorder || closingSession || finishing) {
      return;
    }

    if (!sessionId) {
      await launchBrowserSession();
      return;
    }

    await startRecorder(sessionId);
  };

  const stopTestSession = async (silent = false) => {
    if (!testSessionId) {
      setTestStatus('idle');
      setTestResult(null);
      setTestLiveViewUrl(null);
      setNotice(null);
      return;
    }
    if (!silent) setTestLoading(true);
    try {
      await fetch(config.getApiUrl(`/browser/session/${testSessionId}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (!silent) {
        setNotice('Closed test session.');
      } else {
        setNotice(null);
      }
    } catch (e: any) {
      if (!silent) setError(e?.message || 'Failed to stop test session');
    } finally {
      setTestSessionId(null);
      setTestLiveViewUrl(null);
      setTestStatus('idle');
      setTestResult(null);
      if (!silent) setTestLoading(false);
    }
  };

const runTestAutomation = async () => {
    if (!testPlatform) {
      setNotice('Select a platform to test.');
      return;
    }

    let accountIdForRun = testAccountId;
    let sessionIdForRun = '';

    if (testMode === 'account') {
      if (!testAccountId) {
        setNotice('Select an account to run the automation.');
        return;
      }
      const account = testAccount;
      if (!account) {
        setError('Selected account not found.');
        return;
      }
      if (account.platform_id !== testPlatform.id) {
        setError('Account does not belong to the selected platform.');
        return;
      }
      sessionIdForRun = account.context_id || account.browserbase_context_id || '';
      if (!sessionIdForRun) {
        setError('Account is missing a linked session.');
        return;
      }
    } else {
      if (!testSessionBrowserbaseId) {
        setNotice('Select a session to run the automation.');
        return;
      }
      if (!manualUsername.trim() || !manualPassword) {
        setNotice('Provide username and password to apply to this session.');
        return;
      }
      sessionIdForRun = testSessionBrowserbaseId;
    }

    setTestLoading(true);
    setError(null);
    setNotice(null);
    setTestResult(null);
    setTestStatus('launching');

    let launchedSessionId: string | null = null;

    try {
      if (testMode === 'session') {
        accountIdForRun = '';
        setTestAccountId(prev => prev); // leave existing selection intact
      }

      if (testSessionId) {
        await stopTestSession(true);
      }

      const convId = `test_runner_${Date.now()}`;
      const sessionRes = await fetch(config.getApiUrl('/browser/session'), {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ conversation_id: convId, context_id: sessionIdForRun })
      });
      if (!sessionRes.ok) throw new Error(await sessionRes.text());
      const sessionData = await sessionRes.json();

      const sessionIdValue = sessionData.session_id;
      const debugUrlValue = sessionData.debug_url;

      if (!sessionIdValue || !debugUrlValue) {
        throw new Error('Session response missing session or live view URL');
      }

      launchedSessionId = sessionIdValue;
      setTestSessionId(sessionIdValue);
      setTestLiveViewUrl(debugUrlValue);
      setTestStatus('running');
      setNotice('Live view ready. Running login automation...');

      const executePayload: Record<string, unknown> = {
        session_id: sessionIdValue,
        keepalive: true,
      };

      if (testMode === 'session') {
        executePayload.credentials = {
          username: manualUsername.trim(),
          password: manualPassword,
        };
        executePayload.context_id = sessionIdForRun;
      } else {
        executePayload.account_id = accountIdForRun;
      }

      const execRes = await fetch(config.getApiUrl(`/browser/platforms/${testPlatform.id}/test-execute`), {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(executePayload),
      });
      if (!execRes.ok) throw new Error(await execRes.text());
      const execData = await execRes.json();
      console.log('Test automation execute response:', execData);
      setTestStatus('success');
      setTestResult(execData);
      setNotice('Automation finished.');

      if (execData.session_id) {
        setTestSessionId(execData.session_id);
      }

      if (execData.live_view_url && execData.live_view_url !== debugUrlValue) {
        setTestLiveViewUrl(execData.live_view_url);
      }
    } catch (e: any) {
      console.error('Test automation run failed:', e);
      if (!launchedSessionId) {
        setTestSessionId(null);
        setTestLiveViewUrl(null);
      }
      setTestStatus('error');
      setTestResult(null);
      setError(e?.message || 'Failed to run test automation');
      setNotice(null);
    } finally {
      setTestLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'test' && testSessionId) {
      stopTestSession(true).catch(() => {});
    }
  }, [activeTab, testSessionId]);

  // (removed obsolete modal handlers)

  return (
    <div className="h-full bg-zinc-950 flex flex-col">
      <div className="bg-zinc-900 px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
        <div>
          <h1 className="text-zinc-200 text-lg font-semibold">Login Automations</h1>
          <p className="text-zinc-400 text-sm">Create and bind login flows to your saved sessions.</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            className="text-sm font-medium text-zinc-400 hover:text-zinc-200 transition-colors duration-200"
            onClick={() => window.open('/', '_blank')}
          >
            ← Back to Chat
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-6 pt-4">
        <div className="inline-flex items-center gap-2 bg-zinc-900/60 border border-zinc-800 rounded-lg p-1">
          {[
            { key: 'create', label: 'Create Automation' },
            { key: 'test', label: 'Test Automation' },
            { key: 'hyperbrowser', label: 'Spawn Browser' },
            { key: 'accounts', label: 'Session & Accounts' },
            { key: 'platforms', label: 'Platforms' },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key as any)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                activeTab === (t.key as any)
                  ? 'bg-zinc-800 text-zinc-200 border border-zinc-700'
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6 flex-1 flex flex-col space-y-4 overflow-y-auto">
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded text-red-300 text-sm">{error}</div>
        )}
        {notice && (
          <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded text-blue-300 text-sm">{notice}</div>
        )}

        {/* Browser spawning */}
        {activeTab === 'hyperbrowser' && (
          <div className="flex-1 flex flex-col lg:flex-row gap-6 overflow-hidden">
            <div className="w-full lg:max-w-sm flex-shrink-0 space-y-4">
              <div className="flex items-center gap-3">
                <label className="text-zinc-300 text-sm">Select Session as Base (optional):</label>
                <select
                  className="bg-zinc-900 border border-zinc-700 text-zinc-200 text-sm rounded px-3 py-2 w-full max-w-[90vw] sm:max-w-[520px] truncate"
                  disabled={loading || hyperLoading || !!hyperSessionId}
                  value={hyperSelectedSessionId}
                  onChange={(e) => setHyperSelectedSessionId(e.target.value)}
                >
                  <option value="">-- Create brand new session --</option>
                  {sessions.length === 0 && <option value="" disabled>No saved sessions</option>}
                  {sessions.map(sess => {
                    const url = sess.last_url || '';
                    const urlShort = url.length > 60 ? url.slice(0, 57) + '…' : url;
                    const name = sess.display_name || sess.platform_name || 'Unnamed';
                    return (
                      <option key={sess.id} value={sess.session_id}>
                        {sess.session_id}{` — ${name}`}{urlShort ? ` — ${urlShort}` : ''}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="flex items-center gap-2 justify-end">
                {!hyperSessionId && (
                  <button
                    onClick={launchHyperbrowserSession}
                    disabled={hyperLoading}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      hyperLoading
                        ? 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                        : 'bg-emerald-600 text-white hover:bg-emerald-700'
                    }`}
                  >
                    {hyperLoading ? 'Launching…' : 'Start Browser Session'}
                  </button>
                )}
                {hyperSessionId && (
                  <button
                    onClick={() => stopHyperbrowserSession()}
                    disabled={hyperLoading}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border border-zinc-600 text-zinc-300 hover:text-white hover:border-zinc-400 ${
                      hyperLoading ? 'opacity-60 cursor-not-allowed' : ''
                    }`}
                  >
                    Close Session
                  </button>
                )}
              </div>

              {hyperSessionId && (
                <div className="text-xs text-zinc-400 space-y-1">
                  <div><span className="text-zinc-500">Session ID:</span> {hyperSessionId}</div>
                  {hyperSelectedSession && (
                    <div><span className="text-zinc-500">Session:</span> {hyperSelectedSession.display_name || hyperSelectedSession.session_id}</div>
                  )}
                </div>
              )}
            </div>

            <div className="flex-1 flex flex-col bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
                <div>
                  <h3 className="text-sm text-zinc-200 font-medium">Browser Live View</h3>
                  <p className="text-xs text-zinc-500">
                    {hyperSessionContextId
                      ? `${hyperSelectedSession?.display_name || hyperSelectedSession?.session_id || hyperSessionContextId}`
                      : 'Select a session as base or create a new one'}
                    {hyperSessionId ? ` • ${hyperSessionId}` : ''}
                  </p>
                </div>
              </div>
              <div className="flex-1 bg-black relative overflow-hidden">
                {hyperDebugUrl ? (
                  <iframe
                    key={hyperDebugUrl}
                    src={hyperDebugUrl || 'about:blank'}
                    className="absolute inset-0 h-full w-full"
                    sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
                    allow="clipboard-read; clipboard-write; camera; microphone; fullscreen"
                    allowFullScreen
                    title="Hyperbrowser Live View"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-sm text-zinc-500 px-6 text-center">
                    {hyperLoading
                      ? 'Launching live view…'
                      : 'Launch a browser session to view it here.'}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Platforms table */}
        {activeTab === 'platforms' && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-zinc-200 text-sm font-medium">Your Platforms</h2>
            <div className="flex items-center gap-2">
              <button
                className="text-xs text-zinc-400 hover:text-zinc-200"
                onClick={loadPlatforms}
                disabled={working}
              >
                Refresh
              </button>
            </div>
          </div>
          <div className="overflow-x-auto border border-zinc-800 rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-zinc-900 text-zinc-400">
                <tr>
                  <th className="px-4 py-2 text-left">ID</th>
                  <th className="px-4 py-2 text-left">Platform Name</th>
                  <th className="px-4 py-2 text-left">Login URL</th>
                  <th className="px-4 py-2 text-left">Script Key</th>
                  <th className="px-4 py-2 text-left">Created</th>
                  <th className="px-4 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {platforms.length === 0 ? (
                  <tr>
                    <td className="px-4 py-3 text-zinc-500" colSpan={6}>No platforms found</td>
                  </tr>
                ) : (
                  platforms.map(p => {
                    const created = p.created_at ? new Date(p.created_at).toLocaleString() : '';
                    const url = p.login_url || '';
                    const urlShort = url.length > 80 ? url.slice(0,77) + '…' : url;
                    const key = p.login_script_s3_key || '';
                    const keyShort = key.length > 64 ? key.slice(0,61) + '…' : key;
                    return (
                      <tr key={p.id} className="hover:bg-zinc-900/40">
                        <td className="px-4 py-3 text-zinc-400" title={p.id}>{p.id}</td>
                        <td className="px-4 py-3 text-zinc-200">{p.platform_name}</td>
                        <td className="px-4 py-3 text-zinc-300" title={url}>{urlShort || '—'}</td>
                        <td className="px-4 py-3 text-zinc-400" title={key}>{keyShort || '—'}</td>
                        <td className="px-4 py-3 text-zinc-400">{created || '—'}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button
                              className="rounded-md border border-zinc-700 px-2 py-1 text-xs text-zinc-300 hover:border-zinc-500 hover:text-zinc-100"
                              onClick={() => openPlatformModal(p)}
                            >
                              Edit
                            </button>
                            <button
                              className="rounded-md border border-red-800/70 px-2 py-1 text-xs text-red-300 hover:border-red-500 hover:text-red-200"
                              onClick={() => handleDeletePlatform(p.id)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {activeTab === 'create' && (
        <div className="flex-1 flex flex-col md:flex-row gap-6 overflow-hidden">
          <div className="w-full md:max-w-sm flex-shrink-0 space-y-4 overflow-y-auto">
            <div className="flex items-center gap-3">
              <label className="text-zinc-300 text-sm">Select Session as Base (optional):</label>
              <select
                className="bg-zinc-900 border border-zinc-700 text-zinc-200 text-sm rounded px-3 py-2 w-full max-w-[90vw] sm:max-w-[520px] truncate"
                disabled={loading || working || !!sessionId}
                value={selectedSessionId}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedSessionId(value);
                  if (value) {
                    const u = sessions.find(s => (s.context_id || s.session_id) === value)?.last_url || '';
                    setPlatformUrl(u || '');
                  }
                }}
              >
                <option value="">-- Use temporary session --</option>
                {sessions.length === 0 && <option value="" disabled>No saved sessions</option>}
                {sessions.map(sess => {
                  const url = sess.last_url || '';
                  const urlShort = url.length > 60 ? url.slice(0, 57) + '…' : url;
                  const name = sess.display_name || sess.platform_name || 'Unnamed';
                  return (
                    <option key={sess.id} value={sess.session_id}>
                      {sess.session_id}{` — ${name}`}{urlShort ? ` — ${urlShort}` : ''}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <button className={`px-3 py-1.5 rounded text-sm ${platformAction==='create'?'bg-zinc-800 text-zinc-200 border border-zinc-700':'text-zinc-400 hover:text-zinc-200'}`} onClick={() => setPlatformAction('create')}>Create New Platform</button>
                <button className={`px-3 py-1.5 rounded text-sm ${platformAction==='bind'?'bg-zinc-800 text-zinc-200 border border-zinc-700':'text-zinc-400 hover:text-zinc-200'}`} onClick={() => setPlatformAction('bind')}>Choose Existing Platform</button>
              </div>
              {platformAction === 'create' ? (
                <div className="grid gap-3">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Platform Name</label>
                    <input className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-zinc-200" value={platformName} onChange={e=>setPlatformName(e.target.value)} placeholder="e.g., Delta Dental" />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">
                      Homepage URL <span className="text-zinc-500">(Immediate URL post-login)</span>
                    </label>
                    <input className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-zinc-200" value={platformUrl} onChange={e=>setPlatformUrl(e.target.value)} placeholder="https://example.com/dashboard" />
                  </div>
                </div>
              ) : (
                <div className="grid gap-3">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Existing Platform</label>
                    <select className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-zinc-200" value={selectedPlatformId} onChange={e=>setSelectedPlatformId(e.target.value)}>
                      <option value="">-- Select --</option>
                      {platforms.map(p => (<option key={p.id} value={p.id}>{p.platform_name}</option>))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">
                        Homepage URL <span className="text-zinc-500">(Immediate URL post-login)</span>
                      </label>                  <input className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-zinc-200" value={platformUrl} onChange={e=>setPlatformUrl(e.target.value)} placeholder="https://example.com/dashboard" />
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3 pb-4">
              <button
                onClick={handlePrimaryAction}
                disabled={
                  recordingStarted
                    ? (finishing || closingSession ||
                        (platformAction === 'create' && (!platformName.trim() || !platformUrl.trim())) ||
                        (platformAction === 'bind' && !selectedPlatformId) ||
                        testLoading)
                    : (working || startingRecorder || closingSession || finishing || testLoading || !!testSessionId ||
                        (platformAction === 'create' && (!platformName.trim() || !platformUrl.trim())) ||
                        (platformAction === 'bind' && !selectedPlatformId))
                }
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  recordingStarted
                    ? ((finishing || closingSession ||
                        (platformAction === 'create' && (!platformName.trim() || !platformUrl.trim())) ||
                        (platformAction === 'bind' && !selectedPlatformId) ||
                        testLoading)
                        ? 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                        : 'bg-emerald-600 text-white hover:bg-emerald-700')
                    : ((working || startingRecorder || closingSession || finishing ||
                        (platformAction === 'create' && (!platformName.trim() || !platformUrl.trim())) ||
                        (platformAction === 'bind' && !selectedPlatformId))
                        ? 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                        : 'bg-emerald-600 text-white hover:bg-emerald-700')
                }`}
              >
                {recordingStarted
                  ? (finishing ? 'Saving…' : 'Finish Recording')
                  : (working ? 'Launching…' : startingRecorder ? 'Starting…' : sessionId ? 'Start Recording' : 'Launch Browser')}
              </button>

              <button
                onClick={cancelRecording}
                disabled={!sessionId || closingSession || finishing || startingRecorder || working}
                className={`px-4 py-2 rounded-lg text-sm font-medium border border-zinc-600 text-zinc-300 hover:text-white hover:border-zinc-400 ${(!sessionId || closingSession || finishing || startingRecorder || working) ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                Cancel Recording
              </button>
            </div>
          </div>

          <div className="flex-1 flex flex-col bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
              <div>
                <h3 className="text-sm text-zinc-200 font-medium">Live View</h3>
                <p className="text-xs text-zinc-500">
                  {selectedSession ? selectedSession.display_name || selectedSession.id : 'Select a session'}
                  {sessionId ? ` • ${sessionId}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2">
              </div>
            </div>
            <div className="flex-1 bg-black relative overflow-hidden">
              {debugUrl ? (
                <div className="absolute inset-0">
                  <iframe
                    key={debugUrl ?? 'live-view'}
                    src={debugUrl || 'about:blank'}
                    className="absolute inset-0 h-full w-full"
                    sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
                    allow="clipboard-read; clipboard-write; camera; microphone; fullscreen"
                    allowFullScreen
                    title="Login Automation Live View"
                  />
                </div>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-sm text-zinc-500 px-6 text-center">
                  {sessionId ? 'Waiting for live view…' : 'Start recording to view the live browser session here.'}
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        {activeTab === 'test' && (
        <div className="flex-1 flex flex-col md:flex-row gap-6 overflow-hidden">
          <div className="w-full md:max-w-sm flex-shrink-0 space-y-4 overflow-y-auto">
            <div className="flex items-center gap-2">
              <button
                className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                  testMode === 'account'
                    ? 'bg-zinc-800 text-zinc-200 border border-zinc-700'
                    : 'text-zinc-400 hover:text-zinc-200'
                }`}
                onClick={() => setTestMode('account')}
                disabled={testLoading}
              >
                Use Saved Account
              </button>
              <button
                className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                  testMode === 'session'
                    ? 'bg-zinc-800 text-zinc-200 border border-zinc-700'
                    : 'text-zinc-400 hover:text-zinc-200'
                }`}
                onClick={() => setTestMode('session')}
                disabled={testLoading}
              >
                Use Session & Credentials
              </button>
            </div>

            <div>
              <label className="block text-sm text-zinc-400 mb-1">Platform</label>
              <select
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-zinc-200"
                value={testPlatformId}
                onChange={(e) => setTestPlatformId(e.target.value)}
                disabled={testLoading}
              >
                <option value="">-- Select platform --</option>
                {platforms.map(p => (
                  <option key={p.id} value={p.id}>{p.platform_name}</option>
                ))}
              </select>
            </div>

            {testMode === 'account' ? (
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Account</label>
                <select
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-zinc-200"
                  value={testAccountId}
                  onChange={(e) => setTestAccountId(e.target.value)}
                  disabled={testLoading || accounts.length === 0}
                >
                  <option value="">-- Select account --</option>
                  {accounts
                    .filter(acc => !testPlatformId || acc.platform_id === testPlatformId)
                    .map(acc => (
                      <option key={acc.id} value={acc.id}>
                        {acc.platform_name ? `${acc.platform_name} — ` : ''}{acc.username || acc.id}
                      </option>
                    ))}
                </select>
                {testPlatformId && accounts.every(acc => acc.platform_id !== testPlatformId) && (
                  <p className="text-xs text-amber-400 mt-1">No accounts are linked to this platform yet.</p>
                )}
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Session</label>
                  <select
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-zinc-200"
                    value={testSessionBrowserbaseId}
                    onChange={(e) => setTestSessionBrowserbaseId(e.target.value)}
                    disabled={testLoading || sessions.length === 0}
                  >
                    <option value="">-- Select session --</option>
                    {sessions.map(sess => {
                      const url = sess.last_url || '';
                      const urlShort = url.length > 60 ? `${url.slice(0, 57)}…` : url;
                      const name = sess.display_name || sess.platform_name || 'Unnamed';
                      return (
                        <option key={sess.id} value={sess.session_id}>
                          {sess.session_id}{` — ${name}`}{urlShort ? ` — ${urlShort}` : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="grid gap-2">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Username</label>
                    <input
                      value={manualUsername}
                      onChange={(e) => setManualUsername(e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-zinc-200"
                      placeholder="jane.doe@example.com"
                      disabled={testLoading}
                      autoComplete="username"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Password</label>
                    <input
                      type="password"
                      value={manualPassword}
                      onChange={(e) => setManualPassword(e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-zinc-200"
                      placeholder="Enter password"
                      disabled={testLoading}
                      autoComplete="current-password"
                    />
                  </div>
                </div>
              </>
            )}

            <div className="flex items-center gap-3">
              <button
                onClick={runTestAutomation}
                disabled={!canRunTest}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  canRunTest ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                }`}
              >
                {testLoading ? 'Running…' : 'Run Automation'}
              </button>
              {testSessionId && (
                <button
                  onClick={() => stopTestSession()}
                  disabled={testLoading}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border border-zinc-600 text-zinc-300 hover:text-white hover:border-zinc-400 ${testLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  Stop
                </button>
              )}
            </div>

            <div className="text-xs text-zinc-400 space-y-1 bg-zinc-900/40 border border-zinc-800 rounded-lg px-3 py-2">
              <div className="font-medium text-zinc-300">Status: {testStatus.toUpperCase()}</div>
              {testStatus === 'launching' && <div>Launching live view…</div>}
              {testStatus === 'running' && <div>Automation in progress… watch the live view.</div>}
              {testStatus === 'success' && <div className="text-emerald-300">Automation completed successfully.</div>}
              {testStatus === 'error' && <div className="text-red-300">Automation failed. Check console logs.</div>}
              {testResult?.cookies && (
                <div className="mt-1 text-zinc-300">
                  <span className="font-medium text-zinc-200">Cookies:</span> {testResult.cookies}
                </div>
              )}
              {testResult?.local_storage && (
                <div className="mt-1 text-zinc-300">
                  <span className="font-medium text-zinc-200">Local Storage:</span>{' '}
                  <code className="break-words text-xs text-zinc-400">
                    {JSON.stringify(testResult.local_storage)}
                  </code>
                </div>
              )}
              {testResult?.session_storage && (
                <div className="mt-1 text-zinc-300">
                  <span className="font-medium text-zinc-200">Session Storage:</span>{' '}
                  <code className="break-words text-xs text-zinc-400">
                    {JSON.stringify(testResult.session_storage)}
                  </code>
                </div>
              )}
              {testResult?.platform_url && (
                <div className="mt-1 text-zinc-300">
                  <span className="font-medium text-zinc-200">Platform Login URL:</span> {testResult.platform_url}
                </div>
              )}
              {Array.isArray(testResult?.request_tokens) && testResult.request_tokens.length > 0 && (
                <div className="mt-1 text-zinc-300">
                  <div className="font-medium text-zinc-200 mb-1">Request Tokens:</div>
                  <div className="grid gap-1">
                    {testResult.request_tokens.map((token: any, idx: number) => (
                      <code
                        key={idx}
                        className="block rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-xs text-zinc-400 break-words"
                      >
                        {JSON.stringify(token)}
                      </code>
                    ))}
                  </div>
                </div>
              )}
              {testResult?.status && (
                <div className="mt-1 text-zinc-300">
                  <span className="font-medium text-zinc-200">Backend Status:</span> {testResult.status}
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 flex flex-col bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
              <div>
                <h3 className="text-sm text-zinc-200 font-medium">Live View</h3>
                <p className="text-xs text-zinc-500">
                  {testPlatform ? testPlatform.platform_name : 'Platform'}
                  {selectedTestSession ? ` • ${selectedTestSession.display_name}` : ''}
                  {testAccount ? ` • ${testAccount.username || testAccount.id}` : (testMode === 'session' && manualUsername ? ` • ${manualUsername}` : '')}
                  {testSessionId ? ` • ${testSessionId}` : ''}
                </p>
              </div>
            </div>

            <div className="flex-1 bg-black relative overflow-hidden">
              {testLiveViewUrl ? (
                <div className="absolute inset-0">
                  <iframe
                    key={testLiveViewUrl ?? 'live-view'}
                    src={testLiveViewUrl || 'about:blank'}
                    className="absolute inset-0 h-full w-full"
                    sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
                    allow="clipboard-read; clipboard-write; camera; microphone; fullscreen"
                    allowFullScreen
                    title="Login Automation Test View"
                  />
                </div>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-sm text-zinc-500 px-6 text-center">
                  {testStatus === 'launching'
                    ? 'Launching live view…'
                    : 'Run an automation to view the live browser session here.'}
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        {activeTab === 'accounts' && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-zinc-200 text-sm font-medium">Sessions & Accounts</h2>
            <div className="flex items-center gap-2">
              <button
                className={`px-3 py-1.5 text-xs rounded-md font-medium transition-colors ${
                  platforms.length === 0
                    ? 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                    : 'bg-emerald-600 text-white hover:bg-emerald-700'
                }`}
                onClick={() => openAccountModal()}
                disabled={platforms.length === 0}
              >
                New Account
              </button>
              <button
                className="text-xs text-zinc-400 hover:text-zinc-200"
                onClick={() => { loadSessions(); loadAccounts(); loadPlatforms(); }}
                disabled={working}
              >
                Refresh
              </button>
            </div>
          </div>
          <div className="overflow-x-auto border border-zinc-800 rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-zinc-900 text-zinc-400">
                <tr>
                  <th className="px-4 py-2 text-left">Session Name</th>
                  <th className="px-4 py-2 text-left">Session ID</th>
                  <th className="px-4 py-2 text-left w-[200px]">Last URL</th>
                  <th className="px-4 py-2 text-left">Account ID</th>
                  <th className="px-4 py-2 text-left">Platform</th>
                  <th className="px-4 py-2 text-left w-[240px]">Platform ID</th>
                  <th className="px-4 py-2 text-left">Created</th>
                  <th className="px-4 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {sessions.length === 0 ? (
                  <tr>
                    <td className="px-4 py-3 text-zinc-500" colSpan={8}>No sessions found</td>
                  </tr>
                ) : (
                  sessions.map(sess => {
                      const matchedAccount = accounts.find(acc => {
                      const targetId = acc.browserbase_context_id || acc.id;
                      if (targetId && targetId === sess.id) return true;
                      if (acc.context_id && acc.context_id === (sess.context_id || sess.session_id)) return true;
                      return false;
                    });
                    const platformName = matchedAccount?.platform_name
                      || sess.platform_name
                      || platforms.find(p => p.login_script_s3_key === (sess as any).login_script_s3_key)?.platform_name;
                    const platformId = matchedAccount?.platform_id
                      || sess.platform_id
                      || platforms.find(p => p.login_script_s3_key === (sess as any).login_script_s3_key)?.id
                      || null;
                    const created = sess.created_at ? new Date(sess.created_at).toLocaleString() : '';
                    const url = sess.last_url || '';
                    const urlShort = url.length > 50 ? url.slice(0, 47) + '…' : url;
                      return (
                      <tr key={sess.id} className="hover:bg-zinc-900/40">
                        <td className="px-4 py-3 text-zinc-200">{sess.display_name}</td>
                        <td className="px-4 py-3 text-zinc-400" title={sess.session_id}>{sess.session_id}</td>
                        <td className="px-4 py-3 text-zinc-300" title={url}>{urlShort || '—'}</td>
                        <td
                          className="px-4 py-3 text-zinc-400"
                          title={matchedAccount?.username || undefined}
                        >
                          {matchedAccount?.id || '—'}
                        </td>
                        <td className="px-4 py-3 text-zinc-300">{platformName || '—'}</td>
                        <td className="px-4 py-3 text-zinc-400" title={platformId || undefined}>{platformId || '—'}</td>
                        <td className="px-4 py-3 text-zinc-400">{created || '—'}</td>
                        <td className="px-4 py-3">
                          {matchedAccount ? (
                            <div className="flex items-center gap-2">
                              <button
                                className="rounded-md border border-zinc-700 px-2 py-1 text-xs text-zinc-300 hover:border-zinc-500 hover:text-zinc-100"
                                onClick={() => openAccountModal(sess.context_id || sess.session_id, matchedAccount)}
                              >
                                Edit
                              </button>
                              <button
                                className="rounded-md border border-red-800/70 px-2 py-1 text-xs text-red-300 hover:border-red-500 hover:text-red-200"
                                onClick={() => handleDeleteAccount(matchedAccount.id)}
                              >
                                Delete
                              </button>
                            </div>
                          ) : (
                            <button
                              className="rounded-md border border-zinc-700 px-2 py-1 text-xs text-zinc-300 hover:border-zinc-500 hover:text-zinc-100"
                              onClick={() => openAccountModal(sess.context_id || sess.session_id)}
                            >
                              Add
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}
      </div>

      {showPlatformModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-lg rounded-xl border border-zinc-800 bg-zinc-950 p-6 shadow-xl">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-zinc-100">
                  {platformModalMode === 'edit' ? 'Edit Platform' : 'Create Platform'}
                </h3>
                <p className="mt-1 text-xs text-zinc-400">
                  Manage platform metadata used when binding or reusing login scripts.
                </p>
              </div>
              <button
                type="button"
                onClick={closePlatformModal}
                className="rounded-md border border-transparent px-2 py-1 text-xs text-zinc-400 hover:text-zinc-200"
                disabled={savingPlatform}
              >
                Close
              </button>
            </div>
            <form onSubmit={handlePlatformModalSubmit} className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-zinc-300">Platform Name</label>
                <input
                  value={platformModalName}
                  onChange={(e) => setPlatformModalName(e.target.value)}
                  className="w-full rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100"
                  placeholder="e.g., Delta Dental"
                  disabled={savingPlatform}
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-zinc-300">Login URL (optional)</label>
                <input
                  value={platformModalUrl}
                  onChange={(e) => setPlatformModalUrl(e.target.value)}
                  className="w-full rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100"
                  placeholder="https://example.com/login"
                  disabled={savingPlatform}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-zinc-300">Login Script Key (optional)</label>
                <input
                  value={platformModalKey}
                  onChange={(e) => setPlatformModalKey(e.target.value)}
                  className="w-full rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100"
                  placeholder="s3://bucket/key"
                  disabled={savingPlatform}
                />
              </div>
              {platformModalError && (
                <div className="rounded-md border border-red-500/60 bg-red-500/10 px-3 py-2 text-xs text-red-300">
                  {platformModalError}
                </div>
              )}
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closePlatformModal}
                  className="rounded-md border border-zinc-700 px-3 py-2 text-sm text-zinc-300 hover:border-zinc-500 hover:text-zinc-100"
                  disabled={savingPlatform}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                    savingPlatform ? 'bg-zinc-700 text-zinc-300 cursor-not-allowed' : 'bg-emerald-600 text-white hover:bg-emerald-700'
                  }`}
                  disabled={savingPlatform}
                >
                  {savingPlatform
                    ? 'Saving…'
                    : platformModalMode === 'edit' ? 'Save Changes' : 'Create Platform'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAccountModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-lg rounded-xl border border-zinc-800 bg-zinc-950 p-6 shadow-xl">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-zinc-100">
                  {accountModalMode === 'edit' ? 'Edit Account' : 'Create Account'}
                </h3>
                <p className="mt-1 text-xs text-zinc-400">
                  Link credentials to a session so automated logins can reuse them.
                </p>
              </div>
              <button
                type="button"
                onClick={closeAccountModal}
                className="rounded-md border border-transparent px-2 py-1 text-xs text-zinc-400 hover:text-zinc-200"
                disabled={creatingAccount}
              >
                Close
              </button>
            </div>
            <form onSubmit={handleAccountModalSubmit} className="space-y-4">
              {accountModalMode === 'create' ? (
                <div className="space-y-2">
                  <div className="rounded-md border border-amber-600/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                    A fresh browser session will be created automatically and linked to this account.
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-zinc-300">Session Name</label>
                    <input
                      value={newAccountDisplayName}
                      onChange={(e) => setNewAccountDisplayName(e.target.value)}
                      className="w-full rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100"
                      placeholder={(() => {
                        const platformName = platforms.find(p => p.id === newAccountPlatformId)?.platform_name;
                        return platformName ? `${platformName} Login Session` : 'New Login Session';
                      })()}
                      disabled={creatingAccount}
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-zinc-300">Session</label>
                    <select
                      value={newAccountSessionId}
                      onChange={(e) => {
                        const value = e.target.value;
                        setNewAccountSessionId(value);
                        const matched = sessions.find(sess => (sess.context_id || sess.session_id) === value);
                        if (matched) {
                          setNewAccountDisplayName(matched.display_name || matched.platform_name || '');
                        }
                      }}
                      className="w-full rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100"
                      disabled={creatingAccount}
                      required
                    >
                      <option value="" disabled>Select a session</option>
                      {sessions.map(sess => (
                        <option key={sess.id} value={sess.context_id || sess.session_id}>
                          {sess.session_id}{sess.display_name ? ` — ${sess.display_name}` : sess.platform_name ? ` — ${sess.platform_name}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-zinc-300">Session Name</label>
                    <input
                      value={newAccountDisplayName}
                      onChange={(e) => setNewAccountDisplayName(e.target.value)}
                      className="w-full rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100"
                      placeholder="Enter session display name"
                      disabled={creatingAccount}
                      required
                    />
                  </div>
                </div>
              )}
              <div>
                <label className="mb-1 block text-xs font-medium text-zinc-300">Platform</label>
                <select
                  value={newAccountPlatformId}
                  onChange={(e) => setNewAccountPlatformId(e.target.value)}
                  className="w-full rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100"
                  disabled={creatingAccount}
                  required
                >
                  <option value="" disabled>Select a platform</option>
                  {platforms.map(plat => (
                    <option key={plat.id} value={plat.id}>{plat.platform_name}</option>
                  ))}
                </select>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-zinc-300">Username</label>
                  <input
                    value={newAccountUsername}
                    onChange={(e) => setNewAccountUsername(e.target.value)}
                    className="w-full rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100"
                    placeholder="jane.doe@example.com"
                    autoComplete="username"
                    disabled={creatingAccount}
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-zinc-300">Password</label>
                  <input
                    type="password"
                    value={newAccountPassword}
                    onChange={(e) => setNewAccountPassword(e.target.value)}
                    className="w-full rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100"
                    placeholder="Enter password"
                    autoComplete="current-password"
                    disabled={creatingAccount}
                    required
                  />
                </div>
              </div>
              {accountModalError && (
                <div className="rounded-md border border-red-500/60 bg-red-500/10 px-3 py-2 text-xs text-red-300">
                  {accountModalError}
                </div>
              )}
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeAccountModal}
                  className="rounded-md border border-zinc-700 px-3 py-2 text-sm text-zinc-300 hover:border-zinc-500 hover:text-zinc-100"
                  disabled={creatingAccount}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                    creatingAccount ? 'bg-zinc-700 text-zinc-300 cursor-not-allowed' : 'bg-emerald-600 text-white hover:bg-emerald-700'
                  }`}
                  disabled={creatingAccount}
                >
                  {creatingAccount
                    ? 'Saving…'
                    : accountModalMode === 'edit' ? 'Save Changes' : 'Create Account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

        {showHyperSaveDialog && (
          <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center">
            <div className="bg-zinc-900 border border-white/10 rounded-xl shadow-xl max-w-md w-full mx-4">
              <div className="bg-white/5 px-6 py-4 border-b border-white/10">
                <h3 className="text-zinc-200 font-semibold text-lg">Save Authentication Information</h3>
              </div>
              <div className="p-6">
                <p className="text-zinc-300 mb-4">
                  Choose a name for this website session.
                </p>
                <input
                  type="text"
                  value={hyperContextName}
                  onChange={(e) => setHyperContextName(e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-600 rounded-lg text-zinc-200 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Shopping Cart Flow, Login Testing, etc."
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && hyperContextName.trim() && !hyperContextCanceling && !hyperContextSaving) {
                      saveHyperContext();
                    } else if (e.key === 'Escape') {
                      cancelHyperContextSaving();
                    }
                  }}
                />
                <div className="text-zinc-400 text-sm mt-3">
                  If your task to integrate requires any form of authentication (i.e. login), the generated API endpoint will require cookies or some form of authentication tokens. Create a unique name for this task that will help you retrieve the auth information automatically when you need it from the cookies tab.
                </div>
              </div>
              <div className="flex gap-3 justify-end p-6 pt-0">
                <button
                  className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={cancelHyperContextSaving}
                  disabled={hyperContextCanceling || hyperContextSaving}
                >
                  {hyperContextCanceling ? 'Canceling...' : 'Cancel'}
                </button>
                <button
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={saveHyperContext}
                  disabled={!hyperContextName.trim() || hyperContextCanceling || hyperContextSaving}
                >
                  {hyperContextSaving ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </span>
                  ) : (
                    '💾 Save Session'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
  );
};

export default LoginAutomation;
